<template>
  <!-- expanded status history: one row per update showing when/by and the
       labeled old -> new values that changed in that update (diffed against
       the next-older entry; the API returns newest-first) -->
  <div class="vst-history">
    <div v-if="history && history.length">
      <div class="vst-hist-row vst-hist-row--changes vst-hist-head">
        <span class="vst-hist-when-cell">Updated / By</span>
        <span class="vst-hist-cell">Changes (old &rarr; new)</span>
      </div>
      <div v-for="(h, idx) in sortedHistory" :key="idx" class="vst-hist-row vst-hist-row--changes">
        <span class="vst-hist-when-cell">
          <span class="vst-hist-when"><span class="vst-sr">Updated </span>{{ fmt(h.processed_time) }}</span>
          <span class="vst-hist-by"><span class="vst-sr">by </span>{{ h.updated_by || '-' }}</span>
        </span>
        <span class="vst-hist-cell vst-hist-changes">
          <template v-if="entryChanges(idx).length">
            <span v-for="(c, ci) in entryChanges(idx)" :key="ci" class="vst-hist-change">
              <span class="vst-hist-change-label">{{ c.label }}</span>
              <span class="vst-hist-change-old">{{ c.from }}</span>
              <i class="fa fa-long-arrow-right vst-hist-change-arrow" aria-hidden="true"></i>
              <span class="vst-hist-change-new">{{ c.to }}</span>
            </span>
          </template>
          <!-- system snapshots written for telemetry bookkeeping (gps/return
               times, expiry) with no operational field change -->
          <span v-else class="vst-hist-nochange">telemetry update, no status change</span>
        </span>
      </div>
    </div>
    <div v-else-if="history" class="vst-history-empty">No status history yet.</div>
    <div v-else class="vst-history-empty">Loading history...</div>
  </div>
</template>

<script>
import moment from 'moment'
import { statusLabel } from '@/utils/vehicle_status_labels'

// the fields worth diffing, in display order. keys match the vehicle-info
// history records (telematics-fed tenants carry obdOdometer/fuel_percent/
// def_percent; SMS-fed ones carry odometer/fuel_level/def_level).
const TRACKED_FIELDS = [
  ['vehicle_status', 'Wash'],
  ['maintenance_status', 'Maint'],
  ['notes', 'Notes'],
  ['wheelchair', 'WC'],
  ['stayanddo', 'S&D'],
  ['tote', 'Tote'],
  ['vehicle_base', 'Base'],
  ['company', 'Company'],
  ['obdOdometer', 'Odometer'],
  ['odometer', 'Odometer'],
  ['fuel_percent', 'Fuel %'],
  ['fuel_level', 'Fuel'],
  ['def_percent', 'DEF %'],
  ['def_level', 'DEF'],
]

export default {
  name: 'VsmStatusHistoryList',

  props: {
    history: { type: Array, default: null },
    datetimeFormat: { type: String, default: 'MM/DD hh:mm A' },
  },

  computed: {
    // the history query's GSI sorts on vehicle_name (all equal), so the API
    // order isn't guaranteed; the diffing below depends on newest-first
    sortedHistory() {
      // parse rather than string-compare: processed_time is usually ISO, but
      // some records carry 'MM/DD hh:mm A' strings that would misorder
      const stamp = value => {
        if (!value) return 0
        const m = String(value).indexOf('T') !== -1
          ? moment(String(value))
          : moment(String(value), ['MM/DD hh:mm A'])
        return m.isValid() ? m.valueOf() : 0
      }
      return (this.history || []).slice().sort(
        (a, b) => stamp(b.processed_time) - stamp(a.processed_time)
      )
    },
  },

  methods: {
    fmt(value) {
      if (!value)
        return '-'
      const m = String(value).indexOf('T') !== -1
        ? moment(String(value))
        : moment(String(value), ['MM/DD hh:mm A'])
      return m.isValid() ? m.format(this.datetimeFormat) : String(value)
    },
    normalize(key, value) {
      if (value === true) return 'Yes'
      if (value === false) return null
      if (value == null || value === '') return null
      if (key === 'vehicle_status' || key === 'maintenance_status')
        return statusLabel(value)
      return String(value)
    },
    entryChanges(idx) {
      const entry = this.sortedHistory[idx]
      const older = this.sortedHistory[idx + 1] || null
      const changes = []
      const seenLabels = new Set()
      for (const [key, label] of TRACKED_FIELDS) {
        // obdOdometer/odometer (etc.) alias the same reading; first present wins
        if (seenLabels.has(label))
          continue
        if (entry[key] == null && (!older || older[key] == null))
          continue
        seenLabels.add(label)
        const cur = this.normalize(key, entry[key])
        const prev = older ? this.normalize(key, older[key]) : null
        if (cur === prev)
          continue
        changes.push({ label, from: prev == null ? '—' : prev, to: cur == null ? '—' : cur })
      }
      return changes
    },
  },
}
</script>
